import React, { useState, useEffect, useRef } from 'react';
import useApiRequest from './useApiRequest.jsx';
import { domain } from './Domain.jsx';
import { useLoadingContext } from './LoadingContext.jsx';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    Plus, Edit, Trash2, Search, Filter, RefreshCw, 
    AlertCircle, CheckCircle, X, Info, Video, Image, 
    Upload, Play, Eye, MoreVertical, FileText
} from 'lucide-react';
import notify from './Nots.jsx';

const HowItWorksForm = ({ onAddOrUpdate, formData, setFormData, editingId, setEditingId, handleCancelEdit, postRequest, putRequest, setLoading }) => {
    const [errors, setErrors] = useState({});
    const [coverPreview, setCoverPreview] = useState(null);
    const [videoPreview, setVideoPreview] = useState(null);

    const validateForm = () => {
        const newErrors = {};
        if (!formData.title.trim()) {
            newErrors.title = 'Title is required';
        } else if (formData.title.trim().length < 3) {
            newErrors.title = 'Title must be at least 3 characters';
        }
        if (!formData.description.trim()) {
            newErrors.description = 'Description is required';
        } else if (formData.description.trim().length < 10) {
            newErrors.description = 'Description must be at least 10 characters';
        }
        if (!editingId && !formData.cover) {
            newErrors.cover = 'Cover image is required';
        }
        if (!editingId && !formData.video) {
            newErrors.video = 'Video file is required';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (event) => {
        const { name, value, type, files } = event.target;
        
        if (type === 'file') {
            const file = files[0];
            if (file) {
                if (name === 'cover') {
                    setCoverPreview(URL.createObjectURL(file));
                } else if (name === 'video') {
                    setVideoPreview(URL.createObjectURL(file));
                }
                setFormData({
                    ...formData,
                    [name]: file
                });
            }
        } else {
            setFormData({
                ...formData,
                [name]: value
            });
            // Clear error when user starts typing
            if (errors[name]) {
                setErrors({ ...errors, [name]: '' });
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        
        setLoading(true);
        try {
            const formDatas = new FormData();
            formDatas.append("title", formData.title.trim());
            formDatas.append("description", formData.description.trim());

        if (formData.cover && formData.cover instanceof File) {
            formDatas.append("cover", formData.cover);
        }
        if (formData.video && formData.video instanceof File) {
            formDatas.append("video", formData.video);
        }

        if (editingId) {
            const response = await putRequest(`${domain}/how-it-works/${editingId}`, formDatas, true);
            setEditingId(null);
                onAddOrUpdate(response.body);
                notify("How it works updated successfully", "success");
        } else {
            const response = await postRequest(`${domain}/how-it-works`, formDatas, true);
                onAddOrUpdate(response.body);
                notify("How it works added successfully", "success");
            }
            
            // Reset form
            setFormData({
                title: '',
                description: '',
                cover: '',
                video: '',
            });
            setCoverPreview(null);
            setVideoPreview(null);
            setErrors({});
        } catch (error) {
            notify("Failed to save how it works", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setFormData({
            title: '',
            description: '',
            cover: '',
            video: '',
        });
        setEditingId(null);
        setCoverPreview(null);
        setVideoPreview(null);
        setErrors({});
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6"
        >
            <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center">
                    <Info className="w-5 h-5 text-teal-600" />
                </div>
                <div>
                    <h3 className="text-lg font-semibold text-gray-800">
                        {editingId ? 'Edit How It Works' : 'Add New How It Works'}
                    </h3>
                    <p className="text-sm text-gray-600">
                        {editingId ? 'Update the how it works information' : 'Create a new how it works guide with video and image'}
                    </p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Title Input */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Title
                    </label>
            <input
                type="text"
                        name="title"
                value={formData.title}
                onChange={handleInputChange}
                        placeholder="Enter a descriptive title..."
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200 ${
                            errors.title ? 'border-red-300 bg-red-50' : 'border-gray-300'
                        }`}
                    />
                    {errors.title && (
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="flex items-center gap-1 mt-1 text-red-600 text-sm"
                        >
                            <AlertCircle className="w-4 h-4" />
                            {errors.title}
                        </motion.div>
                    )}
                </div>

                {/* Description Input */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                    </label>
                    <textarea
                        rows={4}
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        placeholder="Enter a detailed description..."
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200 resize-none ${
                            errors.description ? 'border-red-300 bg-red-50' : 'border-gray-300'
                        }`}
                    />
                    {errors.description && (
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="flex items-center gap-1 mt-1 text-red-600 text-sm"
                        >
                            <AlertCircle className="w-4 h-4" />
                            {errors.description}
                        </motion.div>
                    )}
                </div>

                {/* File Uploads */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Cover Image Upload */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Cover Image
                        </label>
                        <div className="space-y-3">
                            <div className="relative">
                                <input
                                    type="file"
                                    name="cover"
                                    onChange={handleInputChange}
                                    accept="image/*"
                                    className="hidden"
                                    id="cover-upload"
                                />
                                <label
                                    htmlFor="cover-upload"
                                    className="flex items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-teal-400 transition-colors duration-200"
                                >
                                    <div className="text-center">
                                        <Image className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                                        <p className="text-sm text-gray-600">Click to upload cover image</p>
                                    </div>
                                </label>
                            </div>
                            {coverPreview && (
                                <div className="relative">
                                    <img
                                        src={coverPreview}
                                        alt="Cover preview"
                                        className="w-full h-32 object-cover rounded-lg"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setCoverPreview(null);
                                            setFormData({ ...formData, cover: '' });
                                        }}
                                        className="absolute top-2 right-2 p-1 bg-red-500 !text-white rounded-full hover:bg-red-600"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            )}
                            {errors.cover && (
                                <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="flex items-center gap-1 text-red-600 text-sm"
                                >
                                    <AlertCircle className="w-4 h-4" />
                                    {errors.cover}
                                </motion.div>
                            )}
                        </div>
                    </div>

                    {/* Video Upload */}
                <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Video File
                        </label>
                        <div className="space-y-3">
                            <div className="relative">
                                <input
                                    type="file"
                                    name="video"
                                    onChange={handleInputChange}
                                    accept="video/*"
                                    className="hidden"
                                    id="video-upload"
                                />
                                <label
                                    htmlFor="video-upload"
                                    className="flex items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-teal-400 transition-colors duration-200"
                                >
                                    <div className="text-center">
                                        <Video className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                                        <p className="text-sm text-gray-600">Click to upload video</p>
                                    </div>
                                </label>
                            </div>
                            {videoPreview && (
                                <div className="relative">
                                    <video
                                        src={videoPreview}
                                        className="w-full h-32 object-cover rounded-lg"
                                        controls
                                    />
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setVideoPreview(null);
                                            setFormData({ ...formData, video: '' });
                                        }}
                                        className="absolute top-2 right-2 p-1 bg-red-500 !text-white rounded-full hover:bg-red-600"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            )}
                            {errors.video && (
                                <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="flex items-center gap-1 text-red-600 text-sm"
                                >
                                    <AlertCircle className="w-4 h-4" />
                                    {errors.video}
                                </motion.div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                    <button
                        type="submit"
                        className="flex-1 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 !text-white px-6 py-3 rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
                    >
                        {editingId ? (
                            <>
                                <Edit className="w-4 h-4" />
                                Update How It Works
                            </>
                        ) : (
                            <>
                                <Plus className="w-4 h-4" />
                                Add How It Works
                            </>
                        )}
                    </button>
                    {editingId && (
                        <button
                            type="button"
                            onClick={handleCancel}
                            className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
                        >
                            <X className="w-4 h-4" />
                            Cancel
                        </button>
                    )}
            </div>
        </form>
        </motion.div>
    );
};

const HowItWorksList = ({ howItWorks, onDelete, onEdit, searchTerm, setSearchTerm, loading }) => {
    const [sortBy, setSortBy] = useState('title');
    const [sortOrder, setSortOrder] = useState('asc');

    const filteredHowItWorks = howItWorks.filter(howIt =>
        howIt.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        howIt.description.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const sortedHowItWorks = [...filteredHowItWorks].sort((a, b) => {
        const aValue = a[sortBy]?.toLowerCase() || '';
        const bValue = b[sortBy]?.toLowerCase() || '';
        return sortOrder === 'asc' 
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
    });

    if (loading) {
        return (
            <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                    <div key={i} className="bg-white rounded-xl p-6 border border-gray-200 animate-pulse">
                        <div className="space-y-4">
                            <div className="h-6 bg-gray-200 rounded w-3/4"></div>
                            <div className="h-4 bg-gray-200 rounded w-full"></div>
                            <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                            <div className="h-48 bg-gray-200 rounded-lg"></div>
                            <div className="flex gap-2">
                                <div className="w-16 h-8 bg-gray-200 rounded"></div>
                                <div className="w-16 h-8 bg-gray-200 rounded"></div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    if (howItWorks.length === 0) {
        return (
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-12"
            >
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Info className="w-12 h-12 text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">No How It Works Found</h3>
                <p className="text-gray-600 mb-6">Start by adding your first how it works guide</p>
            </motion.div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Search and Filter */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1 relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search how it works..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                        />
                    </div>
                    <div className="flex gap-2">
                        <select
                            value={`${sortBy}-${sortOrder}`}
                            onChange={(e) => {
                                const [field, order] = e.target.value.split('-');
                                setSortBy(field);
                                setSortOrder(order);
                            }}
                            className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                        >
                            <option value="title-asc">Title (A-Z)</option>
                            <option value="title-desc">Title (Z-A)</option>
                            <option value="id-asc">ID (Low-High)</option>
                            <option value="id-desc">ID (High-Low)</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Results Summary */}
            <div className="flex items-center justify-between text-sm text-gray-600">
                <p>Showing {filteredHowItWorks.length} of {howItWorks.length} how it works</p>
                {searchTerm && (
                    <button
                        onClick={() => setSearchTerm('')}
                        className="text-teal-500 hover:text-teal-600 font-medium"
                    >
                        Clear search
                    </button>
                )}
            </div>

            {/* How It Works List */}
            <div className="space-y-6">
                <AnimatePresence>
                    {sortedHowItWorks.map((howIt, index) => (
                        <motion.div
                            key={howIt.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ delay: index * 0.1 }}
                            className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-all duration-200"
                        >
                            <div className="p-6">
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex-1">
                                        <h4 className="text-xl font-semibold text-gray-800 mb-2">{howIt.title}</h4>
                                        <p className="text-gray-600 leading-relaxed">{howIt.description}</p>
                                    </div>
                                    <div className="flex items-center gap-2 ml-4">
                                        <button
                                            onClick={() => onEdit(howIt)}
                                            className="px-4 py-2 bg-teal-50 hover:bg-teal-100 text-teal-600 rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
                                        >
                                            <Edit className="w-4 h-4" />
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => onDelete(howIt.id)}
                                            className="px-4 py-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                            Delete
                                        </button>
                                    </div>
                                </div>
                                
                                {/* Media Preview */}
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                    {howIt.cover && (
                                        <div className="relative">
                                            <img
                                                src={domain + howIt.cover}
                                                alt={howIt.title}
                                                className="w-full h-48 object-cover rounded-lg"
                                            />
                                            <div className="absolute top-2 left-2 bg-black/50 !text-white px-2 py-1 rounded text-xs">
                                                Cover Image
                                            </div>
                                        </div>
                                    )}
                                    {howIt.video && (
                                        <div className="relative">
                                            <video
                                                src={domain + howIt.video}
                                                poster={howIt.cover ? domain + howIt.cover : undefined}
                                                className="w-full h-48 object-cover rounded-lg"
                                                controls
                                            />
                                            <div className="absolute top-2 left-2 bg-black/50 !text-white px-2 py-1 rounded text-xs">
                                                Video
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </AnimatePresence>
            </div>
        </div>
    );
};

const HowItWorksComponent = () => {
    const { setLoading } = useLoadingContext();
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        cover: '',
        video: '',
    });
    const [editingId, setEditingId] = useState(null);
    const [howItWorks, setHowItWorks] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLocalLoading] = useState(false);
    const { postRequest, getRequest, putRequest, deleteRequest } = useApiRequest();

    useEffect(() => {
        fetchHowItWorks();
    }, []);

    const fetchHowItWorks = async () => {
        setLocalLoading(true);
        try {
        const response = await getRequest(`${domain}/how-it-works`);
            setHowItWorks(response.body || []);
        } catch (error) {
            console.error('Error fetching how it works:', error);
            notify("Failed to load how it works", "error");
        } finally {
            setLocalLoading(false);
        }
    };

    const handleAddOrUpdate = (howIt) => {
        const index = howItWorks.findIndex((h) => h.id === howIt.id);
        if (index === -1) {
            setHowItWorks([howIt, ...howItWorks]);
        } else {
            const newHowItWorks = [...howItWorks];
            newHowItWorks[index] = howIt;
            setHowItWorks(newHowItWorks);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this how it works? This action cannot be undone.")) {
            setLoading(true);
            try {
                await deleteRequest(`${domain}/how-it-works/${id}`);
                setHowItWorks(howItWorks.filter((howIt) => howIt.id !== id));
                notify("How it works deleted successfully", "success");
            } catch (error) {
                notify("Failed to delete how it works", "error");
            } finally {
                setLoading(false);
            }
        }
    };

    const handleEdit = (howIt) => {
        setEditingId(howIt.id);
        setFormData({
            title: howIt.title,
            description: howIt.description,
            cover: '',
            video: '',
        });
    };

    const handleCancelEdit = () => {
        setFormData({
            title: '',
            description: '',
            cover: '',
            video: '',
        });
        setEditingId(null);
    };

    return (
        <div className="space-y-6">
            <HowItWorksForm 
                onAddOrUpdate={handleAddOrUpdate}
                formData={formData}
                setFormData={setFormData}
                editingId={editingId}
                setEditingId={setEditingId}
                handleCancelEdit={handleCancelEdit}
                postRequest={postRequest}
                putRequest={putRequest}
                setLoading={setLoading}
            />
            
            <HowItWorksList
                howItWorks={howItWorks}
                onDelete={handleDelete}
                onEdit={handleEdit}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                loading={loading}
            />
        </div>
    );
};

export default HowItWorksComponent;